import { Component } from '@theme/component';

/**
 * Quick filter chips that apply a collection filter in place, without a page navigation.
 *
 * The chips are plain `<a href>` links rendered by sections/ll-quick-filters.liquid, so with
 * JavaScript off (or before hydration) they still work — they just navigate. This component
 * upgrades them into a REMOTE CONTROL for the matching sidebar checkboxes: a chip click ends in
 * `.click()` on one of those inputs, so the stock facet pipeline does everything from there. The
 * two can never disagree, because there is only one control — the chip just presses it.
 *
 * A chip carries one facet value, or SEVERAL separated by `;` (an age band such as
 * "4 години;5 години;6 години") that are selected together — Shopify ORs repeated values of one
 * facet param. Link chips (rendered without `data-value`) are not this component's business:
 * `#chips` never selects them and their click falls through to normal navigation.
 *
 * The chips live in their OWN section, above `main-collection`. That is deliberate — the morph
 * only replaces the collection section, so the chip row is never torn down and there is no
 * flicker. The flip side is that nothing re-renders the chips either, so this component keeps
 * their active state and hrefs in sync itself, from the URL, on:
 *   - a chip click (immediately, before the fetch resolves)
 *   - `shopify:collection:update` (a sidebar facet or a clear-all changed the filters)
 *   - `popstate` (browser back/forward)
 *
 * Chips are EXCLUSIVE: picking one clears the others in the same param, so the row behaves
 * like a segmented control rather than the sidebar's multi-select checkboxes. Re-tapping the
 * active chip clears it. Filters in OTHER params (brand, size, colour) are never touched.
 *
 * Every checkbox but one is set silently — no `change` — and the last one is then `.click()`ed,
 * all in one synchronous tick. So the async morph that replaces those inputs cannot interleave,
 * and the single `change` event serialises a form that already holds the final state. One
 * click, one render. (An earlier attempt set `.checked` and called `updateFilters()` instead;
 * that pushed the right URL but never re-rendered, because assigning `.checked` fires no
 * `change`.)
 *
 * @extends {Component}
 */
class LlQuickFiltersComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('shopify:collection:update', this.#syncFromLocation);
    window.addEventListener('popstate', this.#syncFromLocation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:collection:update', this.#syncFromLocation);
    window.removeEventListener('popstate', this.#syncFromLocation);
  }

  /** @returns {string} The filter param these chips control, e.g. `filter.p.m.ll-custom.gender`. */
  get #param() {
    return this.getAttribute('filter-param') ?? '';
  }

  /** @returns {HTMLAnchorElement[]} Filter chips only — link chips carry no `data-value`. */
  get #chips() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('a[data-value]')]);
  }

  /**
   * The facet values a chip selects together.
   *
   * @param {Element} chip
   * @returns {string[]}
   */
  #valuesOf(chip) {
    return (/** @type {HTMLElement} */ (chip).dataset.value ?? '').split(';').filter(Boolean);
  }

  /**
   * Applies a chip's filter in place. Bound from the markup via `on:click="/applyFilter"`.
   *
   * ⚠️ Takes the event as its ONLY argument. `assets/component.js` passes parsed attribute
   * data as a *first* argument only when the `on:` value contains `?` (which is why
   * `filter-remove-buttons.liquid` writes `on:click="/removeFilter?form="`). With no `?`,
   * a two-parameter signature silently receives `undefined` for the event, throws into the
   * dispatcher's try/catch, and the click falls through to a full page navigation.
   *
   * Falls through to normal navigation when the sidebar facet isn't on the page — e.g. a
   * template with filtering disabled. Never trap the click in that case.
   *
   * ⚠️ The chip is a REMOTE CONTROL for the real sidebar checkboxes — it ends in `.click()` on
   * one input, so the stock `FacetInputsComponent` handles the rest and the two can never
   * disagree. Two dead ends were tried first, both verified broken on the live theme:
   *
   *   - `updateFiltersByURL(url)` — `FacetsFormComponent` builds its URL by serialising the
   *     FORM (`createURLParameters`), so the inputs, not the address bar, are authoritative.
   *     Passing a params-free URL does not clear: grid and sidebar both stayed put.
   *   - setting `input.checked` then calling `updateFilters()` — pushes the right URL but
   *     never re-renders the grid, because assigning `.checked` fires no `change` event.
   *
   * @param {MouseEvent} event
   */
  applyFilter(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const chip = /** @type {Element} */ (event.target).closest('a[data-value]');
    if (!(chip instanceof HTMLAnchorElement)) return;

    const values = this.#valuesOf(chip);

    // Matched by property rather than an attribute selector: the param name contains dots.
    // Both the desktop form and the mobile drawer's copy carry the same facet.
    const inputs = /** @type {HTMLInputElement[]} */ (
      [...document.querySelectorAll('input[type="checkbox"]')].filter(
        (input) => /** @type {HTMLInputElement} */ (input).name === this.#param
      )
    );

    // One input per value, preferring the always-rendered desktop form over the drawer's copy
    // inside a <dialog>. Values with no input on this page are simply not part of the band.
    const targets = values
      .map((value) => {
        const candidates = inputs.filter((input) => input.value === value);
        return candidates.find((input) => !input.closest('dialog')) ?? candidates[0];
      })
      .filter((input) => input instanceof HTMLInputElement);

    // No facet on this page (filtering disabled) — let the link navigate.
    if (targets.length === 0) return;

    event.preventDefault();

    const last = /** @type {HTMLInputElement} */ (targets[targets.length - 1]);
    const wasActive = targets.every((input) => input.checked);

    // Exclusive: clear every input of THIS param that is not part of the band. Silent — no
    // `change` — so the single click below produces exactly one re-render, from a form already
    // in its final state.
    for (const input of inputs) {
      if (!targets.includes(input)) input.checked = false;
    }

    // The band's other inputs are set silently to their final state; `last` is put in the
    // OPPOSITE state so that the one `.click()` toggles it to the right one and fires `change`.
    for (const input of targets) {
      if (input !== last) input.checked = !wasActive;
    }
    last.checked = wasActive;
    last.click();
  }

  #syncFromLocation = () => this.#sync(new URL(window.location.href));

  /**
   * Builds the href a chip should carry: its values become the ONLY values of `#param`, or are
   * removed if the chip is already the active one. Every other filter is preserved. Used for
   * the no-JS fallback and to keep hrefs honest after a change — the actual filtering goes
   * through the checkboxes, not this URL, but the two must agree.
   *
   * @param {string[]} values
   * @param {URL} base - The state to build from. Passed explicitly so callers are never at the
   *   mercy of whether `history.pushState` has already run.
   * @returns {string}
   */
  #urlFor(values, base) {
    const url = new URL(base.href);
    const wasActive = this.#isActive(values, url);

    url.searchParams.delete(this.#param);
    if (!wasActive) {
      for (const value of values) url.searchParams.append(this.#param, value);
    }
    // A filter change invalidates the current page of results.
    url.searchParams.delete('page');

    return `${url.pathname}${url.search}`;
  }

  /**
   * A chip is active only when EVERY one of its values is selected — one sidebar tick inside
   * a band does not claim the chip.
   *
   * @param {string[]} values
   * @param {URL} url
   * @returns {boolean}
   */
  #isActive(values, url) {
    const active = url.searchParams.getAll(this.#param);
    return values.length > 0 && values.every((value) => active.includes(value));
  }

  /**
   * Reflects `url`'s filter state onto the chips, and shows the row's note (if any) while a
   * chip is active.
   *
   * @param {URL} url
   */
  #sync(url) {
    let anyActive = false;

    for (const chip of this.#chips) {
      const values = this.#valuesOf(chip);
      const isActive = this.#isActive(values, url);
      anyActive ||= isActive;

      chip.classList.toggle('ll-quick-filters__chip--active', isActive);
      if (isActive) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
      chip.href = this.#urlFor(values, url);
    }

    const note = this.parentElement?.querySelector('[data-quick-filters-note]');
    if (note instanceof HTMLElement) note.hidden = !anyActive;
  }
}

// Named `*-component` on purpose: assets/component.js only runs its custom-element
// upgrade-race recovery for tags matching that suffix. Without it, a click landing
// before this module executes is dropped silently.
if (!customElements.get('ll-quick-filters-component')) {
  customElements.define('ll-quick-filters-component', LlQuickFiltersComponent);
}
